import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Loader2, Lock, Printer, Save, TriangleAlert } from 'lucide-react';
import type { ConfigPlanner, EstiloVisual } from '@miolos/core';
import {
  ESTILOS_VISUAIS,
  aplicarModelo,
  criarConfigPadrao,
  obterModelo,
  validarConfig,
} from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/auth/AuthContext';
import { useAcesso } from '@/auth/EstadoAcesso';
import { api, urlDaImagem } from '@/lib/api';
import { AVISO_BONUS_REVOGADO, semCadernosDeBonus } from '@/lib/bonus';
import { useImpressao } from '@/impressao/useImpressao';
import { useFilaDoPdf } from '@/impressao/useFilaDoPdf';
import { ProvedorEditor, useEditor } from '@/wizard/EstadoEditor';
import { PainelPrevia } from '@/wizard/PainelPrevia';
import { cn } from '@/lib/utils';

export function EditorMiolo() {
  const { id } = useParams<{ id: string }>();
  const [parametros] = useSearchParams();
  const { usuario } = useAuth();
  const navegar = useNavigate();

  /**
   * Modelo escolhido na área de modelos prontos (`/criar?modelo=...`).
   *
   * Ao abrir, o assistente já começa com a receita do modelo aplicada — o
   * usuário segue direto para os ajustes, sem passar pela galeria.
   */
  const modeloDaUrl = id ? null : parametros.get('modelo');
  const modeloInicial = modeloDaUrl && obterModelo(modeloDaUrl) ? modeloDaUrl : null;

  /**
   * Estilo visual escolhido junto do modelo na galeria (`/criar?modelo=...&estilo=...`).
   * Sem o parâmetro, o modelo mantém o acabamento dele.
   */
  const estiloDaUrl = parametros.get('estilo');
  const estiloInicial =
    estiloDaUrl && (ESTILOS_VISUAIS as readonly string[]).includes(estiloDaUrl)
      ? (estiloDaUrl as EstiloVisual)
      : undefined;

  const configDoZero = useCallback(() => {
    const base = criarConfigPadrao();
    if (!modeloInicial) return base;
    const modelo = obterModelo(modeloInicial);
    const comModelo = aplicarModelo(base, modeloInicial, { estiloVisual: estiloInicial });
    return modelo ? { ...comModelo, nome: modelo.nome } : comModelo;
  }, [modeloInicial, estiloInicial]);

  const [configInicial, setConfigInicial] = useState<ConfigPlanner | null>(id ? null : configDoZero());
  const [erro, setErro] = useState<string | null>(null);
  const [avisoBonus, setAvisoBonus] = useState(false);

  /** A biblioteca de prompts de IA é um bônus do administrador. */
  const temBonus = Boolean(usuario?.promptsIA || usuario?.admin);

  useEffect(() => {
    if (!id) {
      setConfigInicial(configDoZero());
      return;
    }
    let ativo = true;
    api
      .obterMiolo(id)
      .then((resposta) => {
        if (!ativo) return;
        const validacao = validarConfig(resposta.miolo.config);
        const carregada = validacao.config ?? criarConfigPadrao();
        // Sem o bônus o caderno de prompts não abre: sai da config que o
        // assistente e a pré-visualização usam (o miolo salvo não é alterado
        // enquanto o usuário não salvar de novo).
        const semBonus = semCadernosDeBonus(carregada, temBonus);
        setAvisoBonus(semBonus !== carregada);
        setConfigInicial(semBonus);
      })
      .catch((falha) => {
        if (ativo) setErro(falha instanceof Error ? falha.message : 'Não foi possível carregar o miolo.');
      });
    return () => {
      ativo = false;
    };
  }, [id, temBonus, configDoZero]);

  if (erro) {
    return (
      <div className="mx-auto max-w-lg space-y-3 py-16 text-center">
        <TriangleAlert className="mx-auto size-6 text-destructive" />
        <p className="text-sm text-muted-foreground">{erro}</p>
        <Button asChild variant="contorno">
          <Link to="/meus-miolos">Voltar para meus miolos</Link>
        </Button>
      </div>
    );
  }

  if (!configInicial) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center gap-3 text-muted-foreground">
        <Loader2 className="size-5 animate-spin" />
        <span className="text-sm">Abrindo o miolo...</span>
      </div>
    );
  }

  const salvar = async (config: ConfigPlanner, atualizarConfig: (c: ConfigPlanner) => void) => {
    if (!usuario) {
      navegar('/entrar', { state: { de: id ? `/miolos/${id}/editar` : '/criar' } });
      return;
    }
    const validacao = validarConfig(config);
    if (!validacao.sucesso) {
      throw new Error(validacao.erros.map((item) => `${item.campo}: ${item.mensagem}`).join(' · '));
    }
    if (id) {
      await api.atualizarMiolo(id, { nome: config.nome, config });
      atualizarConfig(config);
    } else {
      const resposta = await api.criarMiolo({ nome: config.nome, config });
      atualizarConfig(config);
      navegar(`/miolos/${resposta.miolo.id}`);
    }
  };

  return (
    <ProvedorEditor configInicial={configInicial} modeloInicial={modeloInicial}>
      <EditorInterno idExistente={id} aoSalvar={salvar} avisoBonus={avisoBonus} />
    </ProvedorEditor>
  );
}

function EditorInterno({
  idExistente,
  aoSalvar,
  avisoBonus,
}: {
  idExistente?: string;
  aoSalvar: (config: ConfigPlanner, atualizarConfig: (c: ConfigPlanner) => void) => Promise<void>;
  /** O miolo tinha o caderno de prompts e o bônus não está liberado. */
  avisoBonus?: boolean;
}) {
  const {
    config,
    documento,
    paleta,
    passo,
    passos,
    passosDefinidos,
    totalPassos,
    irParaPasso,
    avancar,
    voltar,
    definirConfig,
    atualizar,
  } = useEditor();
  const { marcaDagua, textoMarcaDagua, detalheMarcaDagua, demo, bloqueado } = useAcesso();
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState<{ tom: 'ok' | 'erro'; texto: string } | null>(null);

  /** Fila do PDF no servidor: pedir não trava a tela e o arquivo chega depois. */
  const fila = useFilaDoPdf({ mioloId: idExistente ?? null, nome: config.nome });

  const { imprimir, imprimindo, areaImpressao } = useImpressao({
    documento,
    paleta,
    config,
    resolverImagem: urlDaImagem,
    marcaDagua: marcaDagua ? textoMarcaDagua : null,
    marcaDaguaDetalhe: detalheMarcaDagua,
  });

  const PassoAtual = passos[passo]!.Componente;

  /**
   * Pede o PDF ao servidor.
   *
   * Só aparece quando o miolo já existe no servidor: o gerador abre a página de
   * impressão do miolo salvo, então num rascunho ainda não há o que buscar. O
   * pedido volta na hora e o desenho segue no servidor — dá para continuar
   * mexendo nos passos, e o download começa sozinho quando o arquivo fica pronto.
   */
  const baixarPdf = () => {
    if (!idExistente) return;
    setMensagem(null);
    void fila.pedir();
  };

  const salvar = useCallback(async () => {
    setSalvando(true);
    setMensagem(null);
    try {
      await aoSalvar(config, definirConfig);
      setMensagem({ tom: 'ok', texto: 'Miolo salvo na sua área.' });
    } catch (falha) {
      setMensagem({
        tom: 'erro',
        texto: falha instanceof Error ? falha.message : 'Não foi possível salvar agora.',
      });
    } finally {
      setSalvando(false);
    }
  }, [aoSalvar, config, definirConfig]);

  return (
    <div className="space-y-5">
      {areaImpressao}

      {marcaDagua ? (
        <p className="flex flex-wrap items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs leading-relaxed text-destructive">
          <TriangleAlert className="size-4 shrink-0" />
          {demo ? (
            <span>
              <strong className="font-semibold">Versão de demonstração.</strong> As páginas saem com a marca “cópia não
              autorizada” e o acesso é limitado a um por e-mail.
            </span>
          ) : (
            <span>
              <strong className="font-semibold">Sem licença ativa.</strong> As páginas deste miolo saem com a marca
              “cópia não autorizada” até você ativar um plano.
            </span>
          )}
          <Link to="/planos" className="font-semibold underline underline-offset-2">
            liberar o acesso completo
          </Link>
          {demo ? <span className="text-muted-foreground">· {demo.email}</span> : null}
        </p>
      ) : null}

      {bloqueado ? (
        <p className="flex flex-wrap items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs leading-relaxed text-destructive">
          <Lock className="size-4 shrink-0" />
          <span>
            <strong className="font-semibold">Acesso bloqueado.</strong> Você pode ajustar e imprimir o que já
            salvou, mas não gerar novos miolos
            {idExistente ? ' — salvar aqui atualiza este miolo' : ''}. Fale com quem te atende para liberar de novo.
          </span>
        </p>
      ) : null}

      {avisoBonus ? (
        <p className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs leading-relaxed text-amber-700 dark:text-amber-400">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          <span>{AVISO_BONUS_REVOGADO}</span>
        </p>
      ) : null}

      {/* Barra de ações */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-3">
        <div className="flex min-w-0 items-center gap-3">
          <Button asChild variant="fantasma" tamanho="icone" aria-label="Voltar">
            <Link to={idExistente ? '/meus-miolos' : '/'}>
              <ArrowLeft />
            </Link>
          </Button>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{config.nome || 'Novo miolo'}</p>
            <p className="text-xs text-muted-foreground">
              Passo {passo + 1} de {totalPassos} · {passos[passo]!.titulo}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Badge tom="principal">{documento.resumo.totalPaginas} páginas</Badge>
          <Button variant="contorno" onClick={imprimir} disabled={imprimindo}>
            <Printer />
            {imprimindo ? 'Preparando...' : 'Imprimir'}
          </Button>
          <Button onClick={() => void salvar()} disabled={salvando}>
            {salvando ? <Loader2 className="animate-spin" /> : <Save />}
            {idExistente ? 'Salvar alterações' : 'Salvar na minha área'}
          </Button>
        </div>
      </div>

      {mensagem ? (
        <p
          className={cn(
            'rounded-md border p-3 text-xs',
            mensagem.tom === 'ok'
              ? 'border-emerald-300/70 bg-emerald-50 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300'
              : 'border-destructive/40 bg-destructive/10 text-destructive',
          )}
        >
          {mensagem.texto}
        </p>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[200px_minmax(0,1fr)]">
        {/* Lista de passos */}
        <nav aria-label="Etapas" className="lg:sticky lg:top-20 lg:self-start">
          <ol className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible lg:pb-0">
            {passos.map((definicao, indice) => {
              const Icone = definicao.icone;
              const ativo = indice === passo;
              /**
               * Marcada = a etapa já está resolvida: veio pronta do modelo ou do
               * miolo salvo, ou o usuário já passou por ela nesta edição.
               */
              const concluido = passosDefinidos.includes(definicao.id);
              return (
                <li key={definicao.id} className="shrink-0 lg:w-full">
                  <button
                    type="button"
                    onClick={() => irParaPasso(indice)}
                    aria-current={ativo ? 'step' : undefined}
                    title={
                      concluido
                        ? `${definicao.titulo} — etapa já definida`
                        : `${definicao.titulo} — ainda por definir`
                    }
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-md border px-3 py-2 text-left transition-colors',
                      ativo
                        ? 'border-primary bg-primary/[0.06] text-foreground'
                        : 'border-transparent text-muted-foreground hover:bg-secondary',
                    )}
                  >
                    <span
                      className={cn(
                        'flex size-6 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold',
                        ativo
                          ? 'border-primary bg-primary text-primary-foreground'
                          : concluido
                            ? 'border-success/40 bg-success/10 text-success'
                            : 'border-border',
                      )}
                    >
                      {concluido ? <Check className="size-3" strokeWidth={3} /> : <Icone className="size-3" />}
                    </span>
                    <span className="hidden min-w-0 lg:block">
                      <span className="block truncate text-xs font-medium">{definicao.titulo}</span>
                    </span>
                    <span className="whitespace-nowrap text-xs font-medium lg:hidden">{definicao.titulo}</span>
                  </button>
                </li>
              );
            })}
          </ol>

          {/* Explica a marcação: sem isso o ✓ vira enigma na primeira abertura. */}
          <p className="mt-2 hidden items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground lg:flex">
            <Check className="mt-0.5 size-3 shrink-0 text-success" strokeWidth={3} />
            <span>
              Etapas já definidas — pelo modelo, pelo miolo que você abriu ou por você nesta edição.
              {passosDefinidos.length > 0
                ? ` ${passosDefinidos.length} de ${totalPassos} prontas.`
                : ''}
            </span>
          </p>
        </nav>

        {/*
          Conteúdo + prévia.

          No celular a prévia vem primeiro (ordem trocada por `order-*`): quem
          está decidindo precisa ver a página antes dos controles, e não depois
          de rolar a tela inteira. A partir de `xl` a prévia volta para a
          coluna da direita.
        */}
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
          <div className="order-2 space-y-5 xl:order-1">
            <div className="rounded-lg border border-border bg-card p-4 sm:p-5">
              <PassoAtual />
            </div>

            {/* Barra fixa no celular: Voltar/Continuar sempre ao alcance do dedo. */}
            <div className="sticky bottom-0 z-30 -mx-3 flex items-center justify-between gap-3 border-t border-border bg-background/95 px-3 py-3 backdrop-blur sm:mx-0 sm:rounded-lg sm:border sm:px-3 xl:static xl:border-0 xl:bg-transparent xl:px-0 xl:backdrop-blur-none">
              <Button variant="contorno" onClick={voltar} disabled={passo === 0}>
                <ArrowLeft />
                Voltar
              </Button>
              <Button onClick={avancar} disabled={passo === totalPassos - 1}>
                Continuar
                <ArrowRight />
              </Button>
            </div>
          </div>

          <aside className="order-1 xl:order-2 xl:sticky xl:top-20 xl:self-start">
            <div className="rounded-lg border border-border bg-card p-4">
              <PainelPrevia
                config={config}
                paleta={paleta}
                documento={documento}
                aoImprimir={imprimir}
                imprimindo={imprimindo}
                aoBaixarPdf={idExistente ? baixarPdf : undefined}
                gerandoPdf={fila.ocupado}
                avisoPdf={fila.aviso}
                aoMudarPosicoes={(posicoesLivres) =>
                  // Sem nenhuma caixa mexida o campo sai da configuração: o miolo
                  // volta a ser exatamente o de antes do arrasto.
                  atualizar({
                    posicoesLivres: Object.keys(posicoesLivres).length > 0 ? posicoesLivres : undefined,
                  })
                }
              />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
